import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { NestedTreeControl } from '@angular/cdk/tree';
import { MatTreeNestedDataSource, MatTreeModule } from '@angular/material/tree';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { DEFAULT_ROW_BUDGET, planTreeExpansion, TreeExpansionPlan } from './tree-expansion';
import { Util } from '../../../util/Util';

export interface FileNode {
  name: string;
  children?: FileNode[];
  path: string;
}

@Component({
  selector: 'app-tree-selector',
  standalone: true,
  imports: [CommonModule, MatTreeModule, MatButtonModule, MatIconModule],
  templateUrl: './tree-selector.component.html',
  styleUrls: ['./tree-selector.component.css']
})
export class TreeSelectorComponent implements OnChanges {
  // 1. Input: Accepts your raw JSON structure
  @Input() rawData: any = {};
  @Input() excludeNode: string = '';
  @Input() excludeBranch: string = '';

  /**
   * When set, a selectable row for the tree ROOT is rendered above the
   * folders, emitting `'/'`.
   *
   * Without it the root is unreachable. The tree lists only folders
   * BELOW the root, so an organization with no folders got an empty box
   * and a permanently disabled action button, and a Project already
   * inside a folder could not be moved back to the top level at all —
   * the "My Projects" breadcrumb button in the Move dialogs only
   * appears once some other folder has been picked, which is a
   * chicken-and-egg.
   */
  @Input() rootLabel: string = '';

  /** Shown in place of the tree when there are no folders. */
  @Input() emptyMessage: string = '';

  /**
   * The folder the host is already showing, when it knows one.
   *
   * Its whole ancestor chain is opened on top of whatever the row budget
   * decided, so an operator moving something out of `/Fiction/2026/Drafts`
   * does not have to navigate back to where they already are before they can
   * see it. Spelled either `''` or `'/'` for the root; both are accepted.
   */
  @Input() currentPath: string = '';

  /**
   * How many rows the tree may occupy when it opens itself.
   *
   * A ROW count, not a depth -- see the header of `tree-expansion.ts` for
   * why that distinction is the whole design.
   */
  @Input() expandRowBudget: number = DEFAULT_ROW_BUDGET;

  /** Optional hard ceiling on the auto-expanded depth. Negative means none. */
  @Input() maxAutoExpandDepth: number = -1;

  // 2. Output: Emits the name of the selected node
  @Output() nodeSelected = new EventEmitter<string>();

  selectedNode: FileNode | null = null;

  /** The synthetic node behind {@link rootLabel}. */
  readonly rootNode: FileNode = { name: '', path: '/' };

  treeControl = new NestedTreeControl<FileNode>(node => node.children);
  dataSource = new MatTreeNestedDataSource<FileNode>();

  /** Named folders in the tree. The synthetic root is not one of them. */
  folderCount = 0;

  /** Named folders that have children, i.e. that actually draw a chevron. */
  toggleableCount = 0;

  /** True when every folder with children is open right now. */
  fullyExpanded = false;

  ngOnChanges(changes: SimpleChanges) {
    if (changes['rawData'] && this.rawData) {
      this.dataSource.data = this.transformData(this.rawData, '');

      // `transformData` builds BRAND NEW node objects every time, so whatever
      // the expansion model is holding now refers to nodes that are no longer
      // in the tree: it keeps nothing open and grows without bound.
      this.treeControl.collapseAll();

      // Nothing ever assigned `dataNodes`, which is what the CDK's own
      // `expandAll()` and `getDescendants()` read -- both were silent no-ops.
      this.treeControl.dataNodes = this.dataSource.data;

      this.folderCount = this.countFolders(this.dataSource.data, false);
      this.toggleableCount = this.countFolders(this.dataSource.data, true);
      this.applyAutoExpansion();
    } else if (changes['currentPath'] || changes['expandRowBudget'] || changes['maxAutoExpandDepth']) {
      // Re-planning only ever ADDS expansions. It must never shut a folder the
      // operator opened by hand.
      this.applyAutoExpansion();
    }
  }

  hasChild = (_: number, node: FileNode) => !!node.children && node.children.length > 0;

  /**
   * False when the organization has no folders at all.
   *
   * Counts NAMED folders. The test used to be `dataSource.data.length > 0`,
   * but every host passes `{'': folders.getJSON()}` -- one synthetic root,
   * present whether or not any folder exists -- so it was unconditionally
   * true and `emptyMessage` could never appear.
   */
  get hasNodes(): boolean {
    return this.folderCount > 0;
  }

  /** True when there is anything for an Expand/Collapse control to act on. */
  get hasCollapsibleNodes(): boolean {
    return this.toggleableCount > 0;
  }

  /**
   * The nameless wrapper node standing for the tree root.
   *
   * It is expanded from the start and its own row is not drawn: `rootLabel`
   * is the selectable representation of the root, and a blank row carrying
   * nothing but a chevron is not a second one -- it was just an extra click
   * between the operator and every folder they own.
   */
  isSyntheticRoot(node: FileNode): boolean {
    return !node.name;
  }

  get rootActive(): boolean {
    return this.selectedNode?.path === this.rootNode.path;
  }

  selectNode(node: FileNode) {
    if ( !this.disable(node)) {
      this.selectedNode = node; // Set the local state for highlighting
      this.nodeSelected.emit(node.path);
    }
  }

  selectRoot() {
    if ( !this.disable(this.rootNode) ) {
      this.selectedNode = this.rootNode;
      this.nodeSelected.emit(this.rootNode.path);
    }
  }

  disable(node: FileNode): boolean {
    if ( this.excludeNode && node.path === this.excludeNode ) {
      return true;
    }
    if ( this.inExcludedBranch(node) ) {
      return true;
    }
    return false;
  }

  /**
   * Is this node inside the branch the host excluded?
   *
   * A PATH test, not `startsWith`. Moving a folder called `Test` used to
   * disable a completely unrelated sibling called `Testing` as a destination,
   * because `'/Testing'.startsWith('/Test')` is true -- the same bug
   * `Util.isFolderAtOrUnder` exists to kill everywhere else in the folder
   * code (tests/FolderPathSemantics.smoke.ts). A folder named `Test` next to
   * one named `Testing` is ordinary, not exotic.
   *
   * Deliberately NOT the same question as `disable()`: `excludeNode` greys
   * out one folder whose CHILDREN are still perfectly good destinations, so it
   * must stay expandable. Only a whole excluded branch is dead weight.
   */
  private inExcludedBranch(node: FileNode): boolean {
    return !!this.excludeBranch && Util.isFolderAtOrUnder(node.path, this.excludeBranch);
  }

  // Helper to check if node is active in the template
  isActive(node: FileNode): boolean {
    return this.selectedNode?.path === node.path;
  }

  /** Open the tree as far as {@link planTreeExpansion} judges useful. */
  private applyAutoExpansion(): void {
    const plan: TreeExpansionPlan<FileNode> = planTreeExpansion<FileNode>(this.dataSource.data, {
      rowBudget: this.expandRowBudget,
      maxDepth: this.maxAutoExpandDepth,
      currentPath: this.currentPath
    });
    this.expandAllOf(plan.expand);
    this.fullyExpanded = plan.wholeTree;
  }

  /** Every folder open. Not `treeControl.expandAll()` -- see `dataNodes`. */
  expandAll(): void {
    const plan = planTreeExpansion<FileNode>(this.dataSource.data, { rowBudget: Number.MAX_SAFE_INTEGER });
    this.expandAllOf(plan.expand);
    this.fullyExpanded = true;
  }

  /**
   * Open each node, skipping excluded branches.
   *
   * The planner budgets rows without knowing which of them the host has ruled
   * out -- keeping it that way is what lets it be unit-tested outside Angular.
   * Filtering here instead means an excluded branch costs nothing: the Move
   * Folder dialog would otherwise spend a chunk of its budget unfolding the
   * very folder being moved, every row of it greyed out.
   */
  private expandAllOf(nodes: FileNode[]): void {
    for (const node of nodes) {
      if (this.inExcludedBranch(node)) {
        continue;
      }
      this.treeControl.expand(node);
    }
  }

  /**
   * Back to the top level -- not to nothing.
   *
   * Collapsing the synthetic root leaves an apparently empty box, which is the
   * exact state this whole mechanism exists to prevent, so Collapse stops one
   * level short of it.
   */
  collapseToTopLevel(): void {
    this.treeControl.collapseAll();
    for (const node of this.dataSource.data) {
      if (node.children && node.children.length > 0) {
        this.treeControl.expand(node);
      }
    }
    this.fullyExpanded = false;
  }

  /**
   * Named folders, or only those that draw a chevron when
   * `withChildrenOnly`. The synthetic root is excluded from both counts: it
   * is never drawn, so counting it reports a folder the operator cannot see.
   */
  private countFolders(nodes: FileNode[], withChildrenOnly: boolean): number {
    let count = 0;
    for (const node of nodes || []) {
      const branch = !!(node.children && node.children.length > 0);
      if (node.name && ( !withChildrenOnly || branch )) {
        count++;
      }
      if (branch) {
        count += this.countFolders(node.children, withChildrenOnly);
      }
    }
    return count;
  }

  // Transform logic is kept inside the component for reusability
  private transformData(obj: any, path: string): FileNode[] {
    return Object.keys(obj).map(key => {
      const node: FileNode = { name: key, path: path === '/' ? '/' + key : path + '/' + key };
      const value = obj[key];
      if (value && typeof value === 'object' && Object.keys(value).length > 0) {
        node.children = this.transformData(value, path === '/' ? '/' + key : path + '/' + key);
      }
      return node;
    });
  }
}
